import { RBush3D } from "rbush-3d";
import type { AABB3 } from "./aabb3Types";
import type { BRep, Face } from "./brep";
import { DIST_EPSILON, Line2CollinearOverlap } from "./core";
import { line2CollinearOverlapSegment, line2Eval, line2Len, type Line2Segment } from "./line2";
import { line3SegmentAABB3, type Line3Segment } from "./line3";
import { line2SegmentOnPlane, planeContainsLine3, planeReProjectPolyLine2 } from "./plane";
import { polyLine2Edge, polyLine2EdgeCount, polyLine2Segment } from "./polyLine2";
import { polyLine2New } from "./polyLine2Types";

export interface BRepAdjacencyEdgeRef {
  readonly id: string;
  readonly faceIndex: number;
  readonly loopIndex: number;
  readonly edgeIndex: number;
}

export interface BRepAdjacencyCandidate extends AABB3, BRepAdjacencyEdgeRef {
  readonly brep: BRep;
  readonly face: Face;
  readonly segment2: Line2Segment;
  readonly segment3: Line3Segment;
}

export interface BRepAdjacencyOptions {
  readonly distanceTolerance?: number;
  readonly angleTolerance?: number;
  readonly candidateLengthThreshold?: number;
  readonly predicate?: (candidateA: BRepAdjacencyCandidate, candidateB: BRepAdjacencyCandidate) => boolean;
}

export interface BRepAdjacencyGroupRule {
  readonly queryGroups: readonly string[];
  readonly targetGroups: readonly string[];
}

export interface BRepAdjacency {
  readonly segment: Line3Segment;
  readonly t0: number;
  readonly t1: number;
  readonly candidateA: BRepAdjacencyCandidate;
  readonly candidateB: BRepAdjacencyCandidate;
}

/**
 * Computes shared edge segments between candidate face edges from any number of BRep sources.
 *
 * @param sources - A map of source IDs to BRep adjacency sources.
 * @param edgeRefs - A list of edge references to compute adjacencies for.
 * @param options - Optional options for the adjacency computation.
 * @returns A list of BRep adjacencies.
 */
export function computeBRepAdjacencies(
  sources: Readonly<Record<string, BRep>>,
  edgeRefs: readonly BRepAdjacencyEdgeRef[],
  options: BRepAdjacencyOptions = {},
): BRepAdjacency[] {
  const { distanceTolerance = DIST_EPSILON } = options;
  const adjacencyCandidates = makeBRepAdjacencyCandidates(sources, edgeRefs, options);
  if (adjacencyCandidates.length < 2) {
    return [];
  }

  const index = new RBush3D();
  index.load(adjacencyCandidates);

  return computeBRepAdjacenciesFromCandidateSearches(adjacencyCandidates, index, distanceTolerance, options, new Set());
}

export function computeBRepAdjacenciesForGroups(
  breps: Readonly<Record<string, BRep>>,
  edgeRefsByGroup: Readonly<Record<string, readonly BRepAdjacencyEdgeRef[]>>,
  rules: readonly BRepAdjacencyGroupRule[],
  options: BRepAdjacencyOptions = {},
): BRepAdjacency[] {
  const { distanceTolerance = DIST_EPSILON } = options;
  const candidatesByGroup: Record<string, BRepAdjacencyCandidate[]> = {};

  for (const group in edgeRefsByGroup) {
    candidatesByGroup[group] = makeBRepAdjacencyCandidates(breps, edgeRefsByGroup[group], options);
  }

  const indexesByTargetGroupKey = new Map<string, RBush3D>();
  const seenPairKeys = new Set<string>();
  const result: BRepAdjacency[] = [];

  for (const rule of rules) {
    const queryCandidates = adjacencyCandidatesForGroups(candidatesByGroup, rule.queryGroups);
    const targetGroupKey = bRepAdjacencyGroupKey(rule.targetGroups);
    const index =
      indexesByTargetGroupKey.get(targetGroupKey) ??
      bRepAdjacencyCandidateIndex(adjacencyCandidatesForGroups(candidatesByGroup, rule.targetGroups));
    indexesByTargetGroupKey.set(targetGroupKey, index);
    result.push(
      ...computeBRepAdjacenciesFromCandidateSearches(queryCandidates, index, distanceTolerance, options, seenPairKeys),
    );
  }

  return result;
}

function computeBRepAdjacenciesFromCandidateSearches(
  queryCandidates: readonly BRepAdjacencyCandidate[],
  index: RBush3D,
  distanceTolerance: number,
  options: BRepAdjacencyOptions,
  seenPairKeys: Set<string>,
): BRepAdjacency[] {
  const result: BRepAdjacency[] = [];

  for (const candidateA of queryCandidates) {
    const nearbyCandidates = index.search(
      faceEdgeCandidateSearchBox(candidateA, distanceTolerance),
    ) as BRepAdjacencyCandidate[];

    for (const candidateB of nearbyCandidates) {
      if (candidateA.brep === candidateB.brep) {
        continue;
      }

      const candidateAKey = faceEdgeCandidateRefKey(candidateA);
      const candidateBKey = faceEdgeCandidateRefKey(candidateB);

      const pairKey = faceEdgeCandidatePairKey(candidateAKey, candidateBKey);
      if (seenPairKeys.has(pairKey)) {
        continue;
      }
      seenPairKeys.add(pairKey);

      if (options.predicate && !options.predicate(candidateA, candidateB)) {
        continue;
      }

      const overlap = faceEdgeAdjacencySegment(candidateA, candidateB, options);
      if (!overlap) {
        continue;
      }

      result.push(overlap);
    }
  }

  return result;
}

function adjacencyCandidatesForGroups(
  candidatesByGroup: Readonly<Record<string, readonly BRepAdjacencyCandidate[]>>,
  groups: readonly string[],
): BRepAdjacencyCandidate[] {
  const candidates: BRepAdjacencyCandidate[] = [];
  for (const group of groups) {
    const groupCandidates = candidatesByGroup[group];
    if (groupCandidates) {
      candidates.push(...groupCandidates);
    }
  }
  return candidates;
}

function bRepAdjacencyCandidateIndex(candidates: readonly BRepAdjacencyCandidate[]): RBush3D {
  const index = new RBush3D();
  index.load([...candidates]);
  return index;
}

function bRepAdjacencyGroupKey(groups: readonly string[]): string {
  return [...groups].sort().join(".");
}

export function faceEdgeLine3Segment(face: Face, edgeIndex: number, loopIndex: number = 0): Line3Segment | null {
  const loop = face.loops[loopIndex];
  if (!loop || edgeIndex < 0 || edgeIndex >= polyLine2EdgeCount(loop)) {
    return null;
  }

  return line2SegmentOnPlane(face.plane, polyLine2Segment(polyLine2Edge(loop, edgeIndex)));
}

function makeBRepAdjacencyCandidates(
  breps: Readonly<Record<string, BRep>>,
  candidateRefs: readonly BRepAdjacencyEdgeRef[],
  { candidateLengthThreshold = DIST_EPSILON }: BRepAdjacencyOptions,
): BRepAdjacencyCandidate[] {
  const candidates: BRepAdjacencyCandidate[] = [];

  for (const candidateRef of candidateRefs) {
    const brep = candidateRef ? breps[candidateRef.id] : undefined;
    if (!candidateRef || !brep) {
      continue;
    }

    const candidate = faceEdgeCandidateNew(brep, candidateRef, candidateLengthThreshold);
    if (candidate) {
      candidates.push(candidate);
    }
  }

  return candidates;
}

function faceEdgeCandidateNew(
  brep: BRep,
  candidateRef: BRepAdjacencyEdgeRef,
  candidateLengthThreshold: number,
): BRepAdjacencyCandidate | null {
  const { edgeIndex, faceIndex, loopIndex } = candidateRef;
  const face = brep.faces[faceIndex];
  if (!face) {
    return null;
  }

  const loop = face.loops[loopIndex];
  if (!loop || edgeIndex < 0 || edgeIndex >= polyLine2EdgeCount(loop)) {
    return null;
  }

  const segment2 = polyLine2Segment(polyLine2Edge(loop, edgeIndex));
  const [x0, y0, x1, y1] = segment2;
  if (line2Len(x0, y0, x1, y1) <= candidateLengthThreshold) {
    return null;
  }

  const segment3 = line2SegmentOnPlane(face.plane, segment2);

  return {
    ...line3SegmentAABB3(segment3),
    ...candidateRef,
    brep,
    face,
    segment2,
    segment3,
  };
}

function faceEdgeAdjacencySegment(
  candidateA: BRepAdjacencyCandidate,
  candidateB: BRepAdjacencyCandidate,
  { distanceTolerance = DIST_EPSILON, angleTolerance }: BRepAdjacencyOptions = {},
): BRepAdjacency | null {
  const edgeB = faceEdgeLine2SegmentInFaceBasis(candidateB, candidateA.face, distanceTolerance);
  if (!edgeB) {
    return null;
  }

  const [ax0, ay0, ax1, ay1] = candidateA.segment2;
  const [bx0, by0, bx1, by1] = edgeB;
  const overlap = line2CollinearOverlapSegment(
    ax0,
    ay0,
    ax1,
    ay1,
    bx0,
    by0,
    bx1,
    by1,
    distanceTolerance,
    angleTolerance,
  );
  if (overlap.classification === Line2CollinearOverlap.NONE || overlap.classification === Line2CollinearOverlap.POINT) {
    return null;
  }

  const { t0, t1 } = overlap;

  const [sX0, sY0] = line2Eval(ax0, ay0, ax1, ay1, t0, true);
  const [sX1, sY1] = line2Eval(ax0, ay0, ax1, ay1, t1, true);
  if (line2Len(sX0, sY0, sX1, sY1) <= distanceTolerance) {
    return null;
  }

  const segment = line2SegmentOnPlane(candidateA.face.plane, [sX0, sY0, sX1, sY1]);
  return { segment, t0, t1, candidateA, candidateB };
}

function faceEdgeLine2SegmentInFaceBasis(
  candidate: BRepAdjacencyCandidate,
  targetFace: Face,
  tolerance: number,
): Line2Segment | null {
  if (!planeContainsLine3(targetFace.plane, candidate.segment3, tolerance)) {
    return null;
  }

  return polyLine2Segment(
    planeReProjectPolyLine2(polyLine2New(candidate.segment2), candidate.face.plane, targetFace.plane),
  );
}

function faceEdgeCandidateSearchBox(candidate: BRepAdjacencyCandidate, tolerance: number): AABB3 {
  return {
    minX: candidate.minX - tolerance,
    minY: candidate.minY - tolerance,
    minZ: candidate.minZ - tolerance,
    maxX: candidate.maxX + tolerance,
    maxY: candidate.maxY + tolerance,
    maxZ: candidate.maxZ + tolerance,
  };
}

function faceEdgeCandidateRefKey(candidate: BRepAdjacencyCandidate): string {
  return `${candidate.id}:${candidate.faceIndex}:${candidate.loopIndex}:${candidate.edgeIndex}`;
}

function faceEdgeCandidatePairKey(candidateAKey: string, candidateBKey: string): string {
  if (candidateAKey < candidateBKey) {
    return `${candidateAKey}:${candidateBKey}`;
  }

  return `${candidateBKey}:${candidateAKey}`;
}
